// App.js - Main application router for Travel+ CRM
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { ToastProvider } from './components/common/Toast';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import AgentPasswordResetRequest from './components/Auth/AgentPasswordResetRequest';
import AgentPasswordResetConfirm from './components/Auth/AgentPasswordResetConfirm';
import AgentPasswordResetFlow from './components/Auth/AgentPasswordResetFlow';
import AdminPasswordResetRequest from './components/Auth/AdminPasswordResetRequest';
import AdminPasswordResetConfirm from './components/Auth/AdminPasswordResetConfirm';
import AgentPasswordSetup from './components/Auth/AgentPasswordSetup';

// Public Components
import LandingPage from './components/LandingPage';
import JoinAsAgency from './components/JoinAsAgency';
import IndependentLoginPage from './components/Auth/IndependentLoginPage';
import AgencyLoginPage from './components/Auth/AgencyLoginPage';
import AdminLoginPage from './components/Auth/AdminLoginPage';
import IndependentAgentRegistration from './components/Auth/IndependentAgentRegistration';
import IndependentAgentPricing from './components/Auth/IndependentAgentPricing';
import PublicGroupBooking from './components/Public/PublicGroupBooking';
import GroupPaymentSuccess from './components/Public/GroupPaymentSuccess';
import ClientRegistration from './components/Public/ClientRegistration';

// Agent Components
import AgentLayout from './components/Agent/AgentLayout';
import TravelieLayout from './components/Agent/TravelieLayout';
import AgentDashboard from './components/Agent/AgentDashboard';
import ClientsManagement from './components/Agent/ClientsManagement';
import ItinerariesList from './components/Agent/ItinerariesList';
import CommissionsList from './components/Agent/CommissionsList';
import PaymentRemindersList from './components/Agent/PaymentRemindersList';
import PaymentsList from './components/Agent/PaymentsList';
import PaymentLinksList from './components/Agent/PaymentLinksList';
import PaymentReports from './components/Agent/PaymentReports';
import AgentProfile from './components/Agent/AgentProfile';
import AgentAnalytics from './components/Agent/AgentAnalytics';
import SupplierRequestForm from './components/Agent/SupplierRequestForm';
import QuoteBuilder from './components/Quotes/QuoteBuilder';
import QuotesList from './components/Quotes/QuotesList';
import WebsiteWrapper from './components/Agent/WebsiteWrapper';
import WorkflowStages from './components/Agent/WorkflowStages';
import TasksDashboard from './components/Agent/TasksDashboard';
import CommunicationsHistory from './components/Agent/CommunicationsHistory';
import TaskTemplatesManager from './components/Agent/TaskTemplatesManager';
import WorkflowAutomationConfig from './components/Agent/WorkflowAutomationConfig';
import WorkflowAnalytics from './components/Agent/WorkflowAnalytics';
import GroupsManagement from './components/Agent/Groups/GroupsManagement';
import ActiveAgentAI from './components/Agent/ActiveAgentAI';
import AgentSupport from './components/Agent/AgentSupport';
import AIToolsPage from './components/Agent/AIToolsPage';
import CalendarPage from './components/Agent/CalendarPage';
import CrowdCalendar from './components/Agent/CrowdCalendar';
import TripShare from './components/Agent/TripShare/TripShare';
import LeadsManagement from './components/Agent/LeadsManagement';
import SupplierPortalLinks from './components/Agent/SupplierPortalLinks';
import MobileDevConsole from './components/MobileDevConsole';
import { TaxFormSelection } from './components/TaxForms';
import SentryTestButton from './components/Test/SentryTestButton';
import ErrorBoundary from './components/ErrorBoundary/ErrorBoundary';
import CommissionOnlyPage from './components/Agent/CommissionOnlyPage';

// Independent Agent Components
import {
  IndependentLayout,
  IndependentDashboard,
  IndependentClientsManagement,
  IndependentQuotesList,
  IndependentQuoteBuilder,
  IndependentItinerariesList,
  IndependentItineraryBuilder,
  IndependentLeadsManagement,
  IndependentTasksDashboard,
  IndependentPaymentsList,
  IndependentPaymentLinks,
  IndependentPaymentReminders,
  IndependentCommunications,
  IndependentAnalytics,
  IndependentProfile
} from './components/Independent';
import IndependentCommissionsList from './components/IndependentAgent/IndependentCommissionsList';
import SubscriptionManagement from './components/IndependentAgent/SubscriptionManagement';

// Admin Components
import AdminLayout from './components/Admin/AdminLayout';
import AdminDashboard from './components/Admin/AdminDashboard';
import AgentManagement from './components/Admin/AgentManagement';
import AgentBenefits from './components/Admin/AgentBenefits';
import CommissionsManagement from './components/Admin/CommissionsManagement';
import AdminAgentWebsites from './components/Admin/AdminAgentWebsites';
import SupportTickets from './components/Admin/SupportTickets';
import TaxFormsManagement from './components/Admin/TaxFormsManagement';
import AdminAnalytics from './components/Admin/AdminAnalytics';
import SystemLogs from './components/Admin/SystemLogs';
import ErrorLogs from './components/Admin/ErrorLogs';
import AgencySettings from './components/Admin/AgencySettings';
import AgencyAdminDashboard from './components/Admin/AgencyAdminDashboard';
import SuperAdminDashboard from './components/Admin/SuperAdminDashboard';
import AdminCalendar from './components/Admin/AdminCalendar';
import RemittanceManagement from './components/Admin/RemittanceManagement';

// Registration Components
import AgencyRegistration from './components/Registration/AgencyRegistration';
import AgencyRegistrationWithTax from './components/Registration/AgencyRegistrationWithTax';
import RegistrationSuccess from './components/Registration/RegistrationSuccess';

// Public Components
import ModernQuoteView from './components/Public/ModernQuoteView';
import QuoteComparisonView from './components/Public/QuoteComparisonView';
import PublicPaymentPage from './components/Public/PublicPaymentPage';
import PaymentLinkPage from './components/Public/PaymentLinkPage';
import PaymentSuccess from './components/Public/PaymentSuccess';
import TermsOfService from './components/Public/TermsOfService';
import TermsAndConditions from './components/TermsAndConditionsComplete';
import PrivacyPolicy from './components/Public/PrivacyPolicy';
import CategoryPage from './components/Public/CategoryPage';
import ClientPortalDemo from './components/Public/ClientPortalDemo';
import ClientPortal from './components/Public/ClientPortal';

function App() {
  return (
    <AuthProvider>
      <ErrorBoundary>
        <ToastProvider>
        <Router>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/join-as-agency" element={<JoinAsAgency />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/quote/comparison/:quoteNumber" element={<QuoteComparisonView />} />
          <Route path="/quote/:quoteNumber" element={<ModernQuoteView />} />
          <Route path="/public/quote/:quoteNumber" element={<ModernQuoteView />} />
          <Route path="/payment/:sessionId" element={<PublicPaymentPage />} />
          <Route path="/payment-link/:token" element={<PaymentLinkPage />} />
          <Route path="/client/itineraries/:sessionId/payment" element={<PublicPaymentPage />} />          
          <Route path="/payment-success/:confirmationNumber" element={<PaymentSuccess />} />
          <Route path="/registration-success" element={<RegistrationSuccess />} />
          <Route path="/groups/:publicId" element={<PublicGroupBooking />} />
          <Route path="/groups/:publicId/payment-success/:confirmationNumber" element={<GroupPaymentSuccess />} />          
          <Route path="/client-register/:token" element={<ClientRegistration />} />
          
          {/* Client Portal */}
          <Route path="/portal/:token" element={<ClientPortal />} />
          <Route path="/portal-demo" element={<ClientPortalDemo />} />
          
          {/* Public Category Pages */}
          <Route path="/:subdomain/:categorySlug" element={<CategoryPage />} />
          
          {/* Independent Agent Routes */}
          <Route path="/independent" element={<IndependentLoginPage />} />
          <Route path="/independent/login" element={<IndependentLoginPage />} />
          <Route path="/register/independent" element={<IndependentAgentPricing />} />
          <Route path="/register/independent/signup" element={<IndependentAgentRegistration />} />
          
          {/* Protected Independent Agent Routes with Layout */}
          <Route path="/independent" element={
            <ProtectedRoute allowedRoles={['independent_agent']}>
              <IndependentLayout />
            </ProtectedRoute>
          }>
            <Route path="dashboard" element={<IndependentDashboard />} />
            <Route path="clients" element={<IndependentClientsManagement />} />
            <Route path="leads" element={<IndependentLeadsManagement />} />
            <Route path="quotes" element={<IndependentQuotesList />} />
            <Route path="quotes/new" element={<IndependentQuoteBuilder />} />
            <Route path="quotes/:id/edit" element={<IndependentQuoteBuilder />} />
            <Route path="itineraries" element={<IndependentItinerariesList />} />
            {/* Itineraries are created by converting a quote; no standalone create route */}
            <Route path="itineraries/:id/edit" element={<IndependentItineraryBuilder />} />
            <Route path="payments" element={<IndependentPaymentsList />} />
            <Route path="payment-links" element={<IndependentPaymentLinks />} />
            <Route path="payment-reminders" element={<IndependentPaymentReminders />} />
            <Route path="tasks" element={<IndependentTasksDashboard />} />
            <Route path="workflow" element={<div className="p-6"><h1 className="text-2xl font-bold">Workflow</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
            <Route path="communications" element={<IndependentCommunications />} />
            <Route path="groups" element={<div className="p-6"><h1 className="text-2xl font-bold">Group Bookings</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
            <Route path="website" element={<div className="p-6"><h1 className="text-2xl font-bold">My Website</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
            <Route path="analytics" element={<IndependentAnalytics />} />
            <Route path="commissions" element={<IndependentCommissionsList />} />
            <Route path="profile" element={<IndependentProfile />} />
            <Route path="support" element={<div className="p-6"><h1 className="text-2xl font-bold">Support</h1><p className="text-gray-600 mt-2">Coming soon...</p></div>} />
            <Route path="subscription" element={<SubscriptionManagement />} />
          </Route>
          
          {/* Agent Password Reset Routes */}
          <Route path="/agent/forgot-password" element={<AgentPasswordResetFlow />} />
          <Route path="/agent/reset-password" element={<AgentPasswordResetFlow />} />
          <Route path="/:agencyCode/forgot-password" element={<AgentPasswordResetFlow />} />
          <Route path="/:agencyCode/reset-password" element={<AgentPasswordResetFlow />} />
          
          {/* Agent Password Setup Route */}
          <Route path="/setup-password/:token" element={<AgentPasswordSetup />} />
          
          {/* Admin Password Reset Routes */}
          <Route path="/admin/forgot-password" element={<AdminPasswordResetRequest />} />
          <Route path="/admin/reset-password" element={<AdminPasswordResetConfirm />} />
          
          {/* Agency-specific Login Routes */}
          <Route path="/mst/login" element={<AgencyLoginPage agencyCode="MST" agency="MainStreet Travel Agency" color="blue" />} />
          <Route path="/yeti/login" element={<AgencyLoginPage agencyCode="YETI" agency="Yeti Travel Co." color="green" />} />
          <Route path="/bwk/login" element={<AgencyLoginPage agencyCode="BWK" agency="Boardwalk Travel" color="purple" />} />
          
          {/* Agency-specific Registration Routes */}
          <Route path="/mst/register" element={
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
              <div className="max-w-6xl w-full">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">MST</span>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">MainStreet Travel Agency</h1>
                  <p className="text-gray-600">Agent Registration</p>
                </div>
                <AgencyRegistrationWithTax 
                  agency="MainStreet Travel Agency" 
                  agencyCode="MST" 
                  onSuccess={() => window.location.href = '/mst/login'} 
                />
              </div>
            </div>
          } />
          <Route path="/yeti/register" element={
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
              <div className="max-w-6xl w-full">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">YETI</span>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">Yeti Travel Co.</h1>
                  <p className="text-gray-600">Agent Registration</p>
                </div>
                <AgencyRegistrationWithTax 
                  agency="Yeti Travel Co." 
                  agencyCode="YETI" 
                  onSuccess={() => window.location.href = '/yeti/login'} 
                />
              </div>
            </div>
          } />
          <Route path="/bwk/register" element={
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
              <div className="max-w-6xl w-full">
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
                    <span className="text-white font-bold text-xl">BWK</span>
                  </div>
                  <h1 className="text-2xl font-bold text-gray-900">Boardwalk Travel</h1>
                  <p className="text-gray-600">Agent Registration</p>
                </div>
                <AgencyRegistrationWithTax 
                  agency="Boardwalk Travel" 
                  agencyCode="BWK" 
                  onSuccess={() => window.location.href = '/bwk/login'} 
                />
              </div>
            </div>
          } />
          
          {/* Admin Login Routes */}
          <Route path="/admin/login" element={<AdminLoginPage type="super" />} />
          <Route path="/mst/admin" element={<AdminLoginPage type="agency" agencyCode="MST" />} />
          <Route path="/yeti/admin" element={<AdminLoginPage type="agency" agencyCode="YETI" />} />
          <Route path="/bwk/admin" element={<AdminLoginPage type="agency" agencyCode="BWK" />} />
          
          {/* Commission-Only Agent Route (No Layout) */}
          <Route path="/agent/commissions-only" element={
            <ProtectedRoute allowedRoles={['agent']}>
              <CommissionOnlyPage />
            </ProtectedRoute>
          } />

          {/* Travelie Dashboard Route - NEW VERTICAL SIDEBAR LAYOUT */}
          <Route path="/agent/dashboard" element={
            <ProtectedRoute allowedRoles={['agent', 'independent_agent', 'agency_admin', 'super_admin']}>
              <TravelieLayout />
            </ProtectedRoute>
          }>
            <Route index element={<AgentDashboard />} />
          </Route>

          {/* Protected Agent Routes with Layout */}
          {/* MIGRATION: Independent agents now use the same routes as agency agents */}
          <Route path="/agent" element={
            <ProtectedRoute allowedRoles={['agent', 'independent_agent', 'agency_admin', 'super_admin']}>
              <AgentLayout />
            </ProtectedRoute>
          }>
            <Route path="clients" element={<ClientsManagement />} />
            <Route path="workflow" element={<ProtectedRoute><WorkflowStages /></ProtectedRoute>} />
            <Route path="tasks" element={<ProtectedRoute><TasksDashboard /></ProtectedRoute>} />
            <Route path="communications" element={<ProtectedRoute><CommunicationsHistory /></ProtectedRoute>} />
            <Route path="task-templates" element={<ProtectedRoute><TaskTemplatesManager /></ProtectedRoute>} />
            <Route path="workflow-automation" element={<ProtectedRoute><WorkflowAutomationConfig /></ProtectedRoute>} />
            <Route path="leads" element={<LeadsManagement />} />
            <Route path="quotes" element={<QuotesList />} />
            <Route path="quotes/new" element={<QuoteBuilder />} />
            <Route path="quotes/:id" element={<QuoteBuilder />} />
            <Route path="quotes/:id/preview" element={<ModernQuoteView />} />
            <Route path="itineraries" element={<ItinerariesList />} />
            <Route path="commissions" element={<CommissionsList />} />
            <Route path="payment-reminders" element={<PaymentRemindersList />} />
            <Route path="payment-links" element={<PaymentLinksList />} />
            <Route path="payment-reports" element={<PaymentReports />} />
            <Route path="payments" element={<PaymentsList />} />
            <Route path="groups" element={<GroupsManagement />} />
            <Route path="activeagent-ai" element={<ActiveAgentAI />} />
            <Route path="website" element={<WebsiteWrapper />} />
            <Route path="analytics" element={<AgentAnalytics />} />
            <Route path="workflow-analytics" element={<ProtectedRoute><WorkflowAnalytics /></ProtectedRoute>} />
            <Route path="profile" element={<AgentProfile />} />
            <Route path="support" element={<AgentSupport />} />
            <Route path="ai-tools" element={<AIToolsPage />} />
            <Route path="calendar" element={<CalendarPage />} />
            <Route path="tripshare" element={<TripShare />} />
            <Route path="crowd-calendar" element={<CrowdCalendar />} />
            <Route path="supplier-portals" element={<SupplierPortalLinks />} />
            <Route path="tax-forms" element={<TaxFormSelection />} />
            <Route path="supplier-request" element={<SupplierRequestForm />} />
            <Route index element={<Navigate to="/agent/dashboard" replace />} />
          </Route>
          
          {/* Protected Admin Routes with Layout */}
          <Route path="/admin" element={
            <ProtectedRoute allowedRoles={['agency_admin', 'agents_manager', 'commissions_manager', 'super_admin']}>
              <AdminLayout />
            </ProtectedRoute>
          }>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="agents" element={<AgentManagement />} />
            <Route path="benefits" element={<AgentBenefits />} />
            <Route path="calendar" element={<AdminCalendar />} />
            <Route path="commissions" element={<CommissionsManagement />} />
            <Route path="remittances" element={<RemittanceManagement />} />
            <Route path="agent-websites" element={<AdminAgentWebsites />} />
            <Route path="support-tickets" element={<SupportTickets />} />
            <Route path="tax-forms" element={<TaxFormsManagement />} />
            <Route path="analytics" element={<AdminAnalytics />} />
            <Route path="system-logs" element={<SystemLogs />} />
            <Route path="error-logs" element={<ErrorLogs />} />
            <Route path="settings" element={<AgencySettings />} />
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
          </Route>
          
          {/* Legacy Agency Admin Routes */}
          <Route path="/agency/dashboard" element={
            <ProtectedRoute allowedRoles={['agency_admin']}>
              <Navigate to="/admin/dashboard" replace />
            </ProtectedRoute>
          } />
          
          {/* Legacy Super Admin Routes */}
          <Route path="/admin/super/dashboard" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <Navigate to="/admin/dashboard" replace />
            </ProtectedRoute>
          } />
          
          {/* Mobile Development Console - Super Admin Only */}
          <Route path="/mobile-dev" element={
            <ProtectedRoute allowedRoles={['super_admin']}>
              <MobileDevConsole />
            </ProtectedRoute>
          } />
          
          {/* Sentry Test Route - Development Only */}
          <Route path="/sentry-test" element={<SentryTestButton />} />
          
          {/* Default redirect */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </Router>
        </ToastProvider>
      </ErrorBoundary>
    </AuthProvider>
  );
}

export default App;
